'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Treatment } from '@/lib/treatments-data';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star, Shield, Award } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';
import { cn } from '@/lib/utils';

interface HeroSectionProps {
    treatment: Treatment;
}

export default function HeroSection({ treatment }: HeroSectionProps) {
    const { t } = useTranslation('dental_implants');
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const subtitleRef = useRef<HTMLParagraphElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Hero title animation - fade up + slide in
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 60, scale: 0.95 },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1.2,
                    ease: "power3.out",
                    delay: 0.3
                }
            );

            // Subtitle animation
            gsap.fromTo(subtitleRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    delay: 0.6,
                    ease: "power3.out"
                }
            );

            // CTA animation
            gsap.fromTo(ctaRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    delay: 0.9,
                    ease: "power3.out"
                }
            );

            // Floating implant visual animation
            gsap.fromTo(imageRef.current,
                { opacity: 0, scale: 0.8, rotation: -10 },
                {
                    opacity: 1,
                    scale: 1,
                    rotation: 0,
                    duration: 1.5,
                    delay: 0.4,
                    ease: "elastic.out(1, 0.5)"
                }
            );

            // Slow rotation animation for the image
            gsap.to(imageRef.current, {
                rotation: 360,
                duration: 20,
                repeat: -1,
                ease: "none"
            });

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const handleConsultationClick = () => {
        const eligibilitySection = document.getElementById('eligibility');
        eligibilitySection?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            id="hero"
            ref={sectionRef}
            className="relative min-h-[65vh] bg-gradient-to-br from-navy to-navy-950 overflow-hidden"
        >
            {/* Background decorative elements */}
            <div className="absolute inset-0">
                <div className={cn(
                    "absolute top-1/4 w-96 h-96 bg-gold opacity-10 rounded-full blur-3xl",
                    isRTL ? "left-0" : "right-0"
                )}></div>
                <div className={cn(
                    "absolute bottom-1/4 w-80 h-80 bg-gold opacity-10 rounded-full blur-2xl",
                    isRTL ? "right-0" : "left-0"
                )}></div>
            </div>

            <div className="container relative py-10 lg:py-16">
                <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[52vh]">
                    {/* Content */}
                    <div className={cn(
                        "space-y-8 text-center",
                        isRTL ? "lg:text-right" : "lg:text-left"
                    )}>
                        <h1
                            ref={titleRef}
                            className={cn(
                                "text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-serif",
                                isRTL ? "font-arabic" : ""
                            )}
                        >
                            {t('hero.title_part1')}{' '}
                            <span className="text-gold">{t('hero.title_part2')}</span>
                            {t('hero.title_part3') && t('hero.title_part3') !== 'hero.title_part3' && <>{' '}{t('hero.title_part3')}</>}
                        </h1>

                        <p
                            ref={subtitleRef}
                            className={cn(
                                "text-xl md:text-2xl text-slate-300 leading-relaxed max-w-2xl mx-auto",
                                isRTL ? "lg:mr-0 font-arabic" : "lg:ml-0"
                            )}
                        >
                            {t('hero.subtitle')}
                        </p>

                        <div ref={ctaRef} className="space-y-6">
                            <Button
                                onClick={handleConsultationClick}
                                size="lg"
                                className={cn(
                                    "bg-gold hover:bg-white hover:text-navy hover:border-navy text-navy px-8 py-6 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-gold",
                                    isRTL ? "font-arabic" : ""
                                )}
                            >
                                {t('hero.cta_button')}
                                <ArrowRight className={cn(
                                    "w-5 h-5",
                                    isRTL ? "mr-2 rotate-180" : "ml-2"
                                )} />
                            </Button>

                            {/* Trust indicators */}
                            <div className={cn(
                                "flex flex-wrap justify-center items-center gap-6 text-sm text-slate-300",
                                isRTL ? "lg:justify-end" : "lg:justify-start"
                            )}>
                                <div className={cn(
                                    "flex items-center gap-2",
                                    isRTL ? "space-x-reverse" : ""
                                )}>
                                    <Star className="w-5 h-5 text-gold fill-current" />
                                    <span className={cn(isRTL ? "font-arabic" : "")}>{localizeText(t('hero.trust_indicators.rating'))}</span>
                                </div>
                                <div className={cn(
                                    "flex items-center gap-2",
                                    isRTL ? "space-x-reverse" : ""
                                )}>
                                    <Shield className="w-5 h-5 text-gold" />
                                    <span className={cn(isRTL ? "font-arabic" : "")}>{localizeText(t('hero.trust_indicators.warranty'))}</span>
                                </div>
                                <div className={cn(
                                    "flex items-center gap-2",
                                    isRTL ? "space-x-reverse" : ""
                                )}>
                                    <Award className="w-5 h-5 text-gold" />
                                    <span className={cn(isRTL ? "font-arabic" : "")}>{localizeText(t('hero.trust_indicators.experience'))}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Image */}
                    <div className="relative flex justify-center lg:justify-end">
                        <div ref={imageRef} className="relative z-10">
                            <div className="w-80 h-80 md:w-96 md:h-96 relative">
                                <OptimizedImage
                                    src={treatment.heroImage}
                                    alt={t('hero.image_alt')}
                                    width={400}
                                    height={400}
                                    className="rounded-full shadow-2xl border-4 border-gold/20"
                                />
                                {/* Floating dental elements — smaller + tucked
                                    in on mobile so they don't overflow the
                                    image / trigger horizontal scroll. */}
                                <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-12 h-12 sm:w-16 sm:h-16 bg-gold rounded-full flex items-center justify-center shadow-lg">
                                    <span className="text-xl sm:text-2xl">🦷</span>
                                </div>
                                <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                                    <span className="text-lg sm:text-xl">✨</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-slate-300 animate-bounce">
                    <div className="flex flex-col items-center space-y-2">
                        <span className="text-sm">{t('hero.scroll_indicator')}</span>
                        <div className="w-px h-8 bg-gold"></div>
                    </div>
                </div>
            </div>
        </section>
    );
} 
